import { useMemo, useState } from "react";
import {
  CalendarDays, CheckCircle2, ChevronLeft, ChevronRight, Circle, Clock3, KeyRound,
  ListTodo, ShieldAlert, Users
} from "lucide-react";
import {
  buildCalendarDays, calendarMonthStart, calendarMonthTitle, formatDualCalendarDate,
  moveCalendarMonth, type CalendarSystem
} from "../lib/calendar";
import { localDateKey, nextRepeatDate } from "../lib/todo";
import type { SecretRecord, TodoRecord, TodoTask } from "../types";

type CalendarEvent = {
  id: string;
  kind: "todo" | "password";
  dateKey: string;
  title: string;
  time?: string;
  completed?: boolean;
  overdue?: boolean;
  color: string;
  task?: TodoTask;
  secret?: SecretRecord;
};

const jalaliWeekdays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];
const jalaliWeekdayTitles = ["شنبه", "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه"];
const gregorianWeekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const priorityColors = { none: "#7c8797", low: "#3b82f6", medium: "#e79b24", high: "#e45454" } as const;

function expiryDateKey(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.valueOf()) ? value.slice(0, 10) : localDateKey(date);
}

function expiryTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function projectText(task: TodoTask, workspace: TodoRecord): string {
  return (workspace.projects ?? []).find((project) => project.id === task.projectId)?.name ?? "Inbox";
}

export function CalendarApp({ workspace, passwords, onSaveTodo, onOpenPassword, onOpenTodos }: {
  workspace: TodoRecord;
  passwords: SecretRecord[];
  onSaveTodo: (record: TodoRecord) => Promise<TodoRecord>;
  onOpenPassword: (record: SecretRecord) => void;
  onOpenTodos: () => void;
}) {
  const [system, setSystem] = useState<CalendarSystem>(() => localStorage.getItem("nkriz-calendar-system") === "gregorian" ? "gregorian" : "jalali");
  const [anchor, setAnchor] = useState(() => new Date());
  const [selectedKey, setSelectedKey] = useState(() => localDateKey());
  const [savingTask, setSavingTask] = useState("");
  const today = localDateKey();
  const days = useMemo(() => buildCalendarDays(anchor, system), [anchor, system]);
  const title = useMemo(() => calendarMonthTitle(anchor, system), [anchor, system]);
  const events = useMemo(() => {
    const taskEvents: CalendarEvent[] = (workspace.tasks ?? []).filter((task) => Boolean(task.dueDate)).map((task) => ({
      id: `todo:${task.id}`,
      kind: "todo",
      dateKey: task.dueDate,
      title: task.title,
      time: task.dueTime,
      completed: Boolean(task.completedAt),
      overdue: !task.completedAt && task.dueDate < today,
      color: (workspace.projects ?? []).find((project) => project.id === task.projectId)?.color ?? priorityColors[task.priority] ?? priorityColors.none,
      task
    }));
    const passwordEvents: CalendarEvent[] = passwords.filter((record) => !record.deletedAt && Boolean(record.expiresAt)).map((secret) => ({
      id: `password:${secret.id}`,
      kind: "password",
      dateKey: expiryDateKey(secret.expiresAt),
      title: secret.title,
      time: expiryTime(secret.expiresAt),
      overdue: new Date(secret.expiresAt).valueOf() < Date.now(),
      color: "#bd4f71",
      secret
    }));
    return [...taskEvents, ...passwordEvents];
  }, [passwords, today, workspace.projects, workspace.tasks]);
  const eventsByDate = useMemo(() => {
    const grouped = new Map<string, CalendarEvent[]>();
    for (const event of events) grouped.set(event.dateKey, [...(grouped.get(event.dateKey) ?? []), event]);
    for (const entries of grouped.values()) entries.sort((left, right) => (left.time || "99:99").localeCompare(right.time || "99:99") || Number(left.completed) - Number(right.completed));
    return grouped;
  }, [events]);
  const selectedEvents = eventsByDate.get(selectedKey) ?? [];
  const selectedDate = days.find((day) => day.dateKey === selectedKey)?.date ?? new Date(`${selectedKey}T12:00:00`);
  const selectedTitle = formatDualCalendarDate(selectedDate);
  const monthEvents = days.filter((day) => day.inMonth).reduce((sum, day) => sum + (eventsByDate.get(day.dateKey)?.length ?? 0), 0);

  const chooseSystem = (next: CalendarSystem) => {
    setSystem(next);
    localStorage.setItem("nkriz-calendar-system", next);
  };
  const goToday = () => { const now = new Date(); setAnchor(now); setSelectedKey(localDateKey(now)); };
  const chooseDay = (date: Date, dateKey: string, inMonth: boolean) => {
    setSelectedKey(dateKey);
    if (!inMonth) setAnchor(calendarMonthStart(date, system));
  };
  const toggleTask = async (task: TodoTask) => {
    if (savingTask) return;
    setSavingTask(task.id);
    const stamp = new Date().toISOString();
    const completing = !task.completedAt;
    const updated: TodoTask = { ...task, status: completing ? "done" : "todo", completedAt: completing ? stamp : undefined, updatedAt: stamp };
    let tasks = (workspace.tasks ?? []).map((entry) => entry.id === task.id ? updated : entry);
    if (completing && task.repeat !== "none") {
      tasks = [{ ...task, id: crypto.randomUUID(), status: "todo", dueDate: nextRepeatDate(task.dueDate, task.repeat), completedAt: undefined, subtasks: (task.subtasks ?? []).map((subtask) => ({ ...subtask, completed: false })), createdAt: stamp, updatedAt: stamp }, ...tasks];
    }
    try { await onSaveTodo({ ...workspace, tasks, updatedAt: stamp }); }
    finally { setSavingTask(""); }
  };

  const weekdayLabels = system === "jalali" ? jalaliWeekdays : gregorianWeekdays;
  return <div className="calendar-app" data-system={system}>
    <header className="calendar-header">
      <div className="calendar-brand"><span><CalendarDays size={21} /></span><div><strong>Calendar</strong><small>Tasks and password expiries</small></div></div>
      <div className="calendar-navigation"><button onClick={() => setAnchor(moveCalendarMonth(anchor, -1, system))} aria-label="Previous month"><ChevronLeft size={18} /></button><button className="today" onClick={goToday}>Today</button><button onClick={() => setAnchor(moveCalendarMonth(anchor, 1, system))} aria-label="Next month"><ChevronRight size={18} /></button></div>
      <div className="calendar-system" aria-label="Calendar system"><button className={system === "jalali" ? "active" : ""} onClick={() => chooseSystem("jalali")}>جلالی</button><button className={system === "gregorian" ? "active" : ""} onClick={() => chooseSystem("gregorian")}>Gregorian</button></div>
    </header>
    <section className="calendar-title"><div><h2 dir={system === "jalali" ? "rtl" : "ltr"}>{title.primary}</h2><span>{title.secondary}</span></div><p><strong>{monthEvents}</strong> scheduled items this month</p></section>
    <main className="calendar-layout">
      <section className="calendar-month">
        <div className="calendar-weekdays">{weekdayLabels.map((label, index) => <span key={label} title={system === "jalali" ? jalaliWeekdayTitles[index] : label}>{label}</span>)}</div>
        <div className="calendar-grid">{days.map((day) => {
          const dayEvents = eventsByDate.get(day.dateKey) ?? [];
          const selected = day.dateKey === selectedKey;
          return <button key={day.dateKey} className={`calendar-day ${day.inMonth ? "" : "outside"} ${day.dateKey === today ? "current" : ""} ${selected ? "selected" : ""}`} onClick={() => chooseDay(day.date, day.dateKey, day.inMonth)}>
            <span className="calendar-day-number"><b>{day.primaryDay}</b><small>{day.secondaryDay}</small></span>
            <span className="calendar-day-events">{dayEvents.slice(0, 3).map((event) => <i key={event.id} className={`${event.kind} ${event.completed ? "completed" : ""} ${event.overdue ? "overdue" : ""}`} style={{ "--event-color": event.color } as React.CSSProperties}><em />{event.time && <time>{event.time}</time>}<span>{event.title}</span></i>)}{dayEvents.length > 3 && <strong>+{dayEvents.length - 3} more</strong>}</span>
          </button>;
        })}</div>
      </section>
      <aside className="calendar-agenda">
        <div className="calendar-agenda-date"><small>{system === "jalali" ? "روز انتخاب‌شده" : "Selected day"}</small><strong dir="auto">{system === "jalali" ? selectedTitle.jalali : selectedTitle.gregorian}</strong><span dir="auto">{system === "jalali" ? selectedTitle.gregorian : selectedTitle.jalali}</span></div>
        <div className="calendar-agenda-summary"><span><ListTodo size={14} />{selectedEvents.filter((event) => event.kind === "todo").length} tasks</span><span><KeyRound size={14} />{selectedEvents.filter((event) => event.kind === "password").length} expiries</span></div>
        <div className="calendar-agenda-list">{selectedEvents.length ? selectedEvents.map((event) => event.kind === "todo" && event.task ? <article key={event.id} className={`todo ${event.completed ? "completed" : ""} ${event.overdue ? "overdue" : ""}`}>
          <button className="calendar-task-check" onClick={() => void toggleTask(event.task!)} disabled={savingTask === event.task.id} aria-label={event.completed ? "Reopen task" : "Complete task"}>{event.completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}</button>
          <div><strong>{event.title}</strong><span>{event.time && <><Clock3 size={11} />{event.time} · </>}{projectText(event.task, workspace)}{event.task.teamWide && <><Users size={11} />Team</>}</span></div>
        </article> : event.secret ? <button key={event.id} className={`calendar-password-event ${event.overdue ? "overdue" : ""}`} onClick={() => onOpenPassword(event.secret!)}><span><KeyRound size={18} /></span><div><strong>{event.title}</strong><small>{event.overdue ? "Expired" : "Expires"}{event.time ? ` at ${event.time}` : ""} · Open password</small></div><ChevronRight size={15} /></button> : null) : <div className="calendar-agenda-empty"><CalendarDays size={30} /><strong>Nothing scheduled</strong><span>No task deadlines or password expiries on this day.</span></div>}</div>
        <button className="calendar-open-todos" onClick={onOpenTodos}><ListTodo size={15} />Open full To‑Do app</button>
        {selectedKey < today && selectedEvents.some((event) => event.overdue) && <p className="calendar-expiry-note"><ShieldAlert size={14} />This day contains overdue items.</p>}
      </aside>
    </main>
  </div>;
}
